import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatPrice } from '../utils/helpers';
import toast from 'react-hot-toast';
import './CheckoutPage.css';

const METHODS = [
  { key: 'bkash',  label: 'bKash',  color: '#E2136E', bg: '#fce4ef', icon: '💳' },
  { key: 'nagad',  label: 'Nagad',  color: '#F7941D', bg: '#fff3e0', icon: '💰' },
  { key: 'rocket', label: 'Rocket', color: '#8B008B', bg: '#f3e5f5', icon: '🚀' },
  { key: 'upay',   label: 'Upay',   color: '#00A859', bg: '#e8f5e9', icon: '📲' },
];

const CheckoutPage = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [insideDhaka, setInsideDhaka] = useState(true);
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    street: '',
    city: '',
    district: 'Dhaka',
    postalCode: '',
  });
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  // Payment step state
  const [step, setStep] = useState('shipping'); // 'shipping' | 'payment'
  const [placedOrder, setPlacedOrder] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [senderNumber, setSenderNumber] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const shipping = insideDhaka ? 80 : 150;
  const subtotal = cartTotal;
  const total = subtotal + shipping;
  const hasPreOrder = cart.some(i => i.isPreOrder);

  // How much the customer needs to send right now (just shipping charge)
  const amountToSend = shipping;

  useEffect(() => {
    api.get('/payment-settings')
      .then(({ data }) => setPaymentSettings(data))
      .catch(() => {}); // silently ignore if none configured
  }, []);

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // Step 1: Place order, then move to payment step
  const handlePlaceOrder = async e => {
    e.preventDefault();
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }

    setLoading(true);
    try {
      const items = cart.map(i => ({
        product: i.product._id,
        name: i.product.name,
        thumbnail: i.product.thumbnail,
        size: i.size,
        quantity: i.quantity,
        price: i.price,
        isPreOrder: i.isPreOrder,
        preOrderType: i.preOrderType,
      }));

      const advancePaid = hasPreOrder
        ? cart.filter(i => i.isPreOrder).reduce((sum, i) => {
            const p = i.price * i.quantity;
            return sum + (i.preOrderType === '50percent' ? Math.ceil(p * 0.5) : p);
          }, 0) + shipping
        : 0;

      const { data } = await api.post('/orders', {
        items,
        shippingAddress: { ...form, insideDhaka },
        shippingCharge: shipping,
        subtotal,
        totalPrice: total,
        advancePaid,
        paymentMethod: 'Mobile Banking',
        isPreOrder: hasPreOrder,
        note,
      });

      clearCart();
      setPlacedOrder(data);
      setStep('payment');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Customer submits their sender number
  const handleConfirmPayment = async e => {
    e.preventDefault();
    if (!senderNumber.trim()) { toast.error('Please enter the number you sent from'); return; }

    setConfirmingPayment(true);
    try {
      // Save sender number and mark payment as pending
      await api.put(`/orders/${placedOrder._id}/payment-sender`, {
        senderNumber: senderNumber.trim(),
      });
      toast.success('Payment details submitted! Admin will verify shortly.');
      navigate(`/orders/${placedOrder._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save payment info');
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleSkipPayment = () => {
    navigate(`/orders/${placedOrder._id}`);
  };

  const enabledMethods = paymentSettings
    ? METHODS.filter(m => paymentSettings[m.key])
    : [];

  if (cart.length === 0 && step === 'shipping') {
    navigate('/cart');
    return null;
  }

  // ── PAYMENT STEP ────────────────────────────────────────────────────────
  if (step === 'payment') {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="payment-step-wrap">

            {/* Order Confirmed Banner */}
            <div className="order-confirmed-banner">
              <div className="ocb-icon">🎉</div>
              <div>
                <h2>Order Placed Successfully!</h2>
                <p>Order <strong>#{placedOrder?._id?.slice(-8).toUpperCase()}</strong> has been created.</p>
              </div>
            </div>

            {paymentSettings ? (
              <div className="payment-instruction-card card">
                <h3>💸 Send Delivery Fee to Complete Your Order</h3>

                {/* Amount to send */}
                <div className="pay-amount-box">
                  <span className="pay-amount-label">Amount to Send Now</span>
                  <span className="pay-amount-value">{formatPrice(amountToSend)}</span>
                  <span className="pay-amount-note">
                    (Delivery charge only — product payment on delivery)
                  </span>
                </div>

                {/* Send to number */}
                <div className="pay-send-to">
                  <span className="pay-send-label">Send to this Number</span>
                  <div className="pay-number-display">
                    <span className="pay-number-val">{paymentSettings.number}</span>
                    <button
                      className="pay-copy-btn"
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(paymentSettings.number);
                        toast.success('Number copied!');
                      }}
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>

                {/* Available methods */}
                {enabledMethods.length > 0 && (
                  <div className="pay-methods">
                    <span className="pay-methods-label">Available via</span>
                    <div className="pay-methods-chips">
                      {enabledMethods.map(m => (
                        <span
                          key={m.key}
                          className="pay-method-chip"
                          style={{ color: m.color, borderColor: m.color, background: m.bg }}
                        >
                          {m.icon} {m.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {paymentSettings.note && (
                  <p className="pay-extra-note">ℹ️ {paymentSettings.note}</p>
                )}

                <div className="pay-divider" />

                {/* Sender number form */}
                <form onSubmit={handleConfirmPayment} className="pay-sender-form">
                  <p className="pay-sender-instruction">
                    After sending, enter the number you <strong>sent from</strong> below so we can verify your payment:
                  </p>
                  <div className="pay-sender-row">
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label className="form-label">Your Sender Number *</label>
                      <input
                        className="form-input pay-sender-input"
                        placeholder="e.g. 01XXXXXXXXX"
                        value={senderNumber}
                        onChange={e => setSenderNumber(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="pay-sender-actions">
                    <button type="submit" className="btn btn-primary pay-confirm-btn" disabled={confirmingPayment}>
                      {confirmingPayment ? '⏳ Submitting...' : '✅ I Have Sent the Payment'}
                    </button>
                    <button type="button" className="btn btn-ghost pay-skip-btn" onClick={handleSkipPayment}>
                      Pay Later / View Order
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              // No payment settings configured — go to order detail
              <div className="payment-instruction-card card">
                <h3>📦 Order Confirmed!</h3>
                <p style={{ color: 'var(--text3)', marginBottom: 24 }}>
                  Our team will contact you shortly with payment details.
                </p>
                <button className="btn btn-primary" onClick={handleSkipPayment}>
                  View My Order
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── SHIPPING STEP ────────────────────────────────────────────────────────
  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="checkout-title">Checkout</h1>

        <form onSubmit={handlePlaceOrder} className="checkout-layout">
          {/* Shipping Form */}
          <div className="shipping-form card">
            <h3>🚚 Shipping Information</h3>

            <div className="dhaka-toggle">
              <label className={`dhaka-opt ${insideDhaka ? 'active' : ''}`}>
                <input type="radio" checked={insideDhaka} onChange={() => setInsideDhaka(true)} />
                Inside Dhaka (৳80)
              </label>
              <label className={`dhaka-opt ${!insideDhaka ? 'active' : ''}`}>
                <input type="radio" checked={!insideDhaka} onChange={() => setInsideDhaka(false)} />
                Outside Dhaka (৳150)
              </label>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" name="name" value={form.name} onChange={handleChange} required placeholder="Your full name" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input className="form-input" name="phone" value={form.phone} onChange={handleChange} required placeholder="01XXXXXXXXX" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Street Address *</label>
              <input className="form-input" name="street" value={form.street} onChange={handleChange} required placeholder="House/Road/Area" />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">City *</label>
                <input className="form-input" name="city" value={form.city} onChange={handleChange} required placeholder="City" />
              </div>
              <div className="form-group">
                <label className="form-label">District *</label>
                <input className="form-input" name="district" value={form.district} onChange={handleChange} required placeholder="District" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Postal Code</label>
              <input className="form-input" name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="Postal code" />
            </div>

            <div className="form-group">
              <label className="form-label">Order Note (optional)</label>
              <textarea className="form-input" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Any special instructions..." style={{ resize: 'vertical' }} />
            </div>
          </div>

          {/* Order Summary */}
          <div className="checkout-summary">
            <div className="card summary-card">
              <h3>📦 Order Summary</h3>
              <div className="checkout-items">
                {cart.map((item, i) => (
                  <div key={i} className="checkout-item">
                    <img src={item.product.thumbnail || 'https://via.placeholder.com/60'} alt={item.product.name} />
                    <div className="checkout-item-info">
                      <span className="checkout-item-name">{item.product.name}</span>
                      <span className="checkout-item-meta">Size: {item.size} × {item.quantity}</span>
                      {item.isPreOrder && (
                        <span className="checkout-preorder">Pre-Order ({item.preOrderType === '50percent' ? '50%' : '100%'})</span>
                      )}
                    </div>
                    <span className="checkout-item-price">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="checkout-totals">
                <div className="checkout-row"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                <div className="checkout-row"><span>Shipping</span><span>{formatPrice(shipping)}</span></div>
                {hasPreOrder && (
                  <div className="checkout-row preorder-row">
                    <span>Advance Required</span>
                    <span className="adv-price">
                      {formatPrice(
                        cart.filter(i => i.isPreOrder).reduce((s, i) => {
                          const p = i.price * i.quantity;
                          return s + (i.preOrderType === '50percent' ? Math.ceil(p * 0.5) : p);
                        }, 0) + shipping
                      )}
                    </span>
                  </div>
                )}
                <div className="checkout-row total-row">
                  <span>Total</span>
                  <span className="total-price">{formatPrice(total)}</span>
                </div>
              </div>

              {/* Payment preview */}
              {paymentSettings && (
                <div className="checkout-pay-preview">
                  <p className="cpp-title">💳 Payment at Next Step</p>
                  <p className="cpp-desc">
                    You'll send <strong>{formatPrice(shipping)}</strong> (delivery fee only) to{' '}
                    <strong>{paymentSettings.number}</strong>
                  </p>
                  <p className="cpp-methods">
                    {enabledMethods.map(m => m.label).join(' · ')} available
                  </p>
                </div>
              )}

              <button type="submit" className="btn btn-primary place-order-btn" disabled={loading}>
                {loading ? '⏳ Placing Order...' : '🎉 Place Order'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
